/**
 * Summary rail for the weekly proposal screen: resumen (units/refs) + delivery +
 * the read-only note for viewers + the opt-out note where it applies.
 *
 * The cutoff countdown and the save/discard actions used to live here too, but
 * were lifted to ProposalPage so the four pieces — countdown, actions,
 * table, summary — are orderable siblings. That lets the mobile layout reorder
 * them (countdown → actions → table → summary) via CSS grid areas without
 * touching the reconciliation logic.
 *
 * There is no "cancel order" action anywhere: opting out is zeroing every line
 * through the product table, and whether that is even possible depends on the
 * store's floor (`minQuantityPerProduct`). Above zero it is not — a line
 * carries at least the floor — so the note that explains it only renders for a
 * store whose floor is 0, the one that can actually do it.
 */
import React from 'react'
import {formatPostalCode} from '@retail/orders-domain'
import {formatMadridDate} from '../../lib/dateFormat'
import type {ProposalViewModel} from './proposalViewModel'
import styles from './ProposalSidebar.module.scss'

type ProposalSidebarProps = {
  viewModel: ProposalViewModel
  localTotalUnits: number
  localTotalRefs: number
  /** The store's per-line floor — only a 0 leaves the opt-out reachable. */
  minQuantityPerProduct: number
}

export const ProposalSidebar = ({
  viewModel,
  localTotalUnits,
  localTotalRefs,
  minQuantityPerProduct,
}: ProposalSidebarProps): React.ReactElement => {
  const delivery = viewModel.delivery
  const isEditable = viewModel.isEditable

  return (
    <aside className={`col-flx ${styles['proposal-sidebar']}`}>
      {/* Resumen card — live local counts while editing */}
      <div className={`bg-white ${styles['proposal-sidebar__card']}`}>
        <div className={`txt-bold-500 txt-upper txt-light-kale ${styles['proposal-sidebar__overline']}`}>Resumen de la semana</div>
        <div className="row-flx flx-a-b flx-gap-8">
          <span className={`txt-honey txt-olive ${styles['proposal-sidebar__summary-n']}`}>{localTotalUnits}</span>
          <span className={`txt-light-kale ${styles['proposal-sidebar__summary-u']}`}>
            {`${localTotalUnits === 1 ? 'unidad' : 'unidades'} en ${localTotalRefs} ${localTotalRefs === 1 ? 'referencia' : 'referencias'}`}
          </span>
        </div>
      </div>

      {/* Delivery card */}
      <div className={`bg-white ${styles['proposal-sidebar__card']}`}>
        <div className={`txt-bold-500 txt-upper txt-light-kale ${styles['proposal-sidebar__overline']}`}>Entrega</div>

        {delivery !== null ? (
          <>
            <div className={`row-flx flx-j-b ${styles['proposal-sidebar__kv']}`}>
              <span className="txt-light-kale">Día</span>
              <span className={`txt-bold-500 ${styles['proposal-sidebar__val']}`}>
                {formatMadridDate(delivery.option.deliveryDate)}
              </span>
            </div>
            <div className={`row-flx flx-j-b ${styles['proposal-sidebar__kv']}`}>
              <span className="txt-light-kale">Hora</span>
              <span className={`txt-bold-500 ${styles['proposal-sidebar__val']}`}>
                {delivery.option.start} – {delivery.option.end}
              </span>
            </div>
            <div className={`col-flx txt-light-kale ${styles['proposal-sidebar__addr']}`}>
              <span className={styles['proposal-sidebar__addr-line']}>
                {delivery.address.street}, {delivery.address.number}
                {/* `!= null`: a stored floor can be null as well as absent, and neither prints. */}
                {delivery.address.floor != null ? ` ${delivery.address.floor}` : ''}
              </span>
              <span className={styles['proposal-sidebar__addr-line']}>
                {formatPostalCode(delivery.address.zipcode)} {delivery.address.city}
              </span>
            </div>
          </>
        ) : (
          <p className={`m-0 txt-light-kale ${styles['proposal-sidebar__empty']}`}>Entrega no disponible.</p>
        )}
      </div>

      {/* Read-only note for VIEWER role */}
      {!isEditable && viewModel.role === 'VIEWER' && (
        <div className={`txt-a-c txt-italic txt-light-kale ${styles['proposal-sidebar__readonly-note']}`}>
          Solo lectura — no tienes permiso de edición.
        </div>
      )}

      {minQuantityPerProduct === 0 && (
        <p className={`m-0 txt-light-kale ${styles['proposal-sidebar__note']}`}>
          Para no recibir pedido esta semana, pon todas las cantidades a cero y guarda.
        </p>
      )}
    </aside>
  )
}
